import React, {
  useState, useRef, useEffect, useCallback, useMemo,
} from 'react'
import PropTypes from 'prop-types'

import { Box } from '@campaignhub/suit-theme'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faChevronDown } from '@fortawesome/pro-light-svg-icons'

import styles from './styles.module.scss'

const InfiniteSelect = (props) => {
  const {
    value,
    onChange,
    options,
    onLoadMore,
    hasMore,
    loading,
    labelKey,
    valueKey,
    disabled,
    placeholder,
    name,
    fullWidth,
  } = props

  const [isOpen, setIsOpen] = useState(false)
  const containerRef = useRef(null)
  const listRef = useRef(null)

  const selectedLabel = useMemo(() => {
    const match = options.find(opt => String(opt[valueKey]) === String(value))
    return match ? match[labelKey] : ''
  }, [options, value, labelKey, valueKey])

  const handleSelect = useCallback((opt) => {
    onChange(opt[valueKey], opt)
    setIsOpen(false)
  }, [onChange, valueKey])

  const handleScroll = useCallback(() => {
    if (!listRef.current || loading || !hasMore) return

    const { scrollTop, scrollHeight, clientHeight } = listRef.current
    if (scrollTop + clientHeight >= scrollHeight - 20) {
      onLoadMore()
    }
  }, [loading, hasMore, onLoadMore])

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  return (
    <Box
      ref={containerRef}
      flexDirection="row"
      position="relative"
      width={fullWidth !== false ? '100%' : ['100%', 'calc(50% - 4px)']}
      style={{ boxSizing: 'border-box' }}
    >
      <button
        type="button"
        name={name}
        disabled={disabled}
        className={styles.display}
        onClick={() => !disabled && setIsOpen(open => !open)}
        onKeyDown={(e) => {
          if (disabled) return
          if (e.key === 'Escape') setIsOpen(false)
          if (e.key === 'ArrowDown' && !isOpen) setIsOpen(true)
        }}
      >
        {selectedLabel || placeholder}
        <FontAwesomeIcon icon={faChevronDown} className={styles.caret} />
      </button>

      {isOpen && (
        <ul
          ref={listRef}
          role="listbox"
          className={styles['dropdown-list']}
          onScroll={handleScroll}
        >
          {placeholder && (
            <li
              role="option"
              className={styles.option}
              onClick={() => handleSelect({ [valueKey]: '', [labelKey]: '' })}
            >
              {placeholder}
            </li>
          )}

          {options.length > 0 ? options.map((opt) => {
            const isSelected = String(opt[valueKey]) === String(value)
            return (
              <li
                key={String(opt[valueKey])}
                role="option"
                className={`${styles.option} ${isSelected ? styles['option-selected'] : ''}`}
                onClick={() => handleSelect(opt)}
              >
                {opt[labelKey]}
              </li>
            )
          }) : (
            !loading && (
              <li className={styles.status}>
                No results found
              </li>
            )
          )}

          {loading && (
            <li className={styles.status}>
              Loading...
            </li>
          )}
        </ul>
      )}
    </Box>
  )
}

InfiniteSelect.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func.isRequired,
  options: PropTypes.array,
  onLoadMore: PropTypes.func.isRequired,
  hasMore: PropTypes.bool,
  loading: PropTypes.bool,
  labelKey: PropTypes.string,
  valueKey: PropTypes.string,
  disabled: PropTypes.bool,
  placeholder: PropTypes.string,
  name: PropTypes.string,
  fullWidth: PropTypes.bool,
}

InfiniteSelect.defaultProps = {
  value: '',
  options: [],
  hasMore: false,
  loading: false,
  labelKey: 'name',
  valueKey: 'id',
  disabled: false,
  placeholder: 'Please Select...',
  name: '',
  fullWidth: true,
}

export default InfiniteSelect
